// MIDAS V3 — History rail (Phase 1 + UX pass)
//
// Ports `HistoryRail` + `CollapsedRail` from the design handoff:
//   ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//   chat-window.jsx (lines 104-263)
//
// Wired to real data:
//   • Threads come from existing `midasHistoryPageSlice.threads` (it serves
//     both v2 and v3). Optimistic inserts are mirrored into this slice via
//     extraReducers (UX-pass Item 1).
//   • Active thread comes from `midasPage.activeThreadId` (existing v2 slice).
//   • Machine names come from `customerMachinery.machines` (existing slice).
//
// UX-pass items shipped here:
//   Item 1 — optimistic insert is observed via the cross-slice extraReducers
//            in `midasHistoryPageSlice` (touching that slice is the deliberate
//            exception called out in the UX-pass spec).
//   Item 2 — window-focus listener re-fires `fetchHistoryThreads` when the
//            last fetch is >60s old. Timestamp lives in
//            `midasHistoryPage.lastFetchedAt`.
//   Item 3 — loading skeleton rows render during the first fetch.
//   Item 4 — empty-state copy when `loading === false && threads.length === 0`.
//   Item 6 — hover-reveal 3-dot menu on each row (custom V3 dropdown +
//            confirm dialog). Inline-edit on Rename, themed `ConfirmDialogV3`
//            on Delete. Round 6 (2026-05-22) replaced the Radix dropdown
//            from `v2/ui/dropdown-menu` and the native `window.confirm`
//            because both render OUTSIDE `.midas-v3-root` — V3 token CSS
//            variables resolve to `undefined` in that scope, leaving the
//            dropdown transparent and the confirm locked to Chrome's dark
//            system dialog. The replacements (`RailRowMenuV3`,
//            `ConfirmDialogV3`) portal to body and re-wrap in
//            `.midas-v3-root.p-{theme}` so V3 tokens resolve.
//   Item 7 — all 5 bucketOfV3 buckets rendered (today / yesterday / this week /
//            last week / earlier). Previously only 3 rendered.
//   Item 10 — fade animation on title change after `reconcileOptimisticThread`
//            (skipped when the bot title equals the optimistic title).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  fetchHistoryThreads,
  HistoryMachineFilter,
} from "../../midasHistoryPageSlice";
import { Machine } from "../../../machinery/models/models";
import {
  deleteThread,
  loadThread,
  renameThread,
  setActiveThreadId,
} from "../../midasPageSlice";
import { toggleRailCollapsed } from "../../midasV3Slice";
import MidasLogoV3 from "./MidasLogoV3";
import MidasThemeSwitcherV3 from "./MidasThemeSwitcherV3";
import { bucketOfV3, V3DateBucket } from "./v3-utils/threadBuckets";
import RailRowMenuV3, {
  RailRowMenuV3DeleteIcon,
  RailRowMenuV3RenameIcon,
} from "./RailRowMenuV3";
import ConfirmDialogV3 from "./ConfirmDialogV3";

// ============================================================
// Constants
// ============================================================

export const MIDAS_V3_RAIL_EXPANDED_WIDTH = 248;
export const MIDAS_V3_RAIL_COLLAPSED_WIDTH = 56;

/** Item 2 — re-hydrate threshold. 60s matches the "drift after tab return"
 *  heuristic in the UX-pass spec. */
const FOCUS_REHYDRATE_STALE_MS = 60_000;

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/i.test(
    navigator.userAgent || (navigator as any).platform || ""
  );
const MOD_LABEL = IS_MAC ? "⌘B" : "Ctrl B";
const MOD_LABEL_K = IS_MAC ? "⌘K" : "Ctrl K";

// ============================================================
// RailRow — hover-aware thread row with 3-dot menu actions
// ============================================================
//
// MIDAS-V3-BUGS-0519-R4 — hover state driven by React `useState` + inline
// style ternaries because earlier rounds had CSS-class hover selectors that
// lost to hardcoded inline `background: transparent` declarations.
//
// UX pass — Item 6: a 3-dot dropdown replaces the passive `→` chevron.
// Visible only on hover/focus or while the menu is open. Rename uses inline
// edit (Enter to commit, Esc to cancel). Delete fires `onRequestDelete`
// upward — the parent rail owns a single themed ConfirmDialogV3 and lifts
// the prompt out of `window.confirm` (which Chrome renders dark + non-V3).
//
// Round 6 (2026-05-22) — both V3 surfaces previously broke because the V2
// shadcn primitives (Radix DropdownMenu) and the browser's `window.confirm`
// portal/paint OUTSIDE `.midas-v3-root`. CSS variables defined inside the V3
// root resolve to `undefined` once content escapes that scope, leaving the
// dropdown transparent and the confirm dialog locked to Chrome's dark theme.
// Fix: a custom V3 menu (`RailRowMenuV3`) and confirm dialog
// (`ConfirmDialogV3`), both portal'd to body and re-wrapped in
// `.midas-v3-root.p-{theme}` so V3 tokens resolve.
//
// UX pass — Item 10: title-change fade. Each row tracks its last-rendered
// title; when the title changes (e.g. after `reconcileOptimisticThread`
// replaces the optimistic title with the bot-supplied one) the title element
// briefly fades from opacity 0.6 → 1 over 200ms. If the title is unchanged
// (bot title === optimistic title) the animation is skipped — no flicker.
// ============================================================

interface RailRowProps {
  threadId: string;
  title: string;
  metaText: string;
  isPlatform: boolean;
  isActive: boolean;
  onOpen: (threadId: string) => void;
  onRename: (threadId: string, newTitle: string) => void;
  /**
   * Ask the parent rail to open the themed delete confirm dialog. The parent
   * owns a single dialog instance and decides when to actually invoke
   * `dispatch(deleteThread(...))` based on the user's confirm/cancel.
   */
  onRequestDelete: (threadId: string, title: string) => void;
  rowActionsLabel: string;
  renameLabel: string;
  deleteLabel: string;
}

const RailRow: React.FC<RailRowProps> = ({
  threadId,
  title,
  metaText,
  isPlatform,
  isActive,
  onOpen,
  onRename,
  onRequestDelete,
  rowActionsLabel,
  renameLabel,
  deleteLabel,
}) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);

  // ── Item 10: title-evolution fade ─────────────────────────
  // Track the previous title so we can detect a real change and animate.
  // Initialise to the current title so the very first render does NOT
  // animate (we only animate transitions).
  const prevTitleRef = useRef<string>(title);
  const [titleAnim, setTitleAnim] = useState(false);
  useEffect(() => {
    if (prevTitleRef.current !== title) {
      // Real title change (e.g. reconcileOptimisticThread + later refetch
      // landed a bot-supplied title). Animate. If the new title equals the
      // old one we never enter this branch — flicker avoided.
      setTitleAnim(true);
      prevTitleRef.current = title;
      const id = window.setTimeout(() => setTitleAnim(false), 220);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [title]);

  // Keep the draft in sync when an external rename lands while the input
  // is closed.
  useEffect(() => {
    if (!renaming) setDraftTitle(title);
  }, [title, renaming]);

  // Auto-focus + select-all when entering rename mode.
  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  // Background mirrors R4's accent-mix scheme.
  const background = isActive
    ? "color-mix(in oklab, var(--midas-v3-accent) 22%, var(--midas-v3-card-2))"
    : hovered || menuOpen
    ? "color-mix(in oklab, var(--midas-v3-accent) 16%, var(--midas-v3-card-2))"
    : "transparent";

  const borderLeftColor =
    isActive || hovered || menuOpen
      ? "var(--midas-v3-accent)"
      : "transparent";

  // Menu button visible on hover/focus, or while menu is open, or while
  // renaming (so the user has a visual handle on the row they're acting on).
  // Inactive rows hide it at rest to keep the rail visually quiet.
  const menuButtonOpacity = hovered || menuOpen || renaming ? 1 : 0;

  const titleColor =
    hovered || menuOpen || isActive
      ? "var(--midas-v3-ink)"
      : "var(--midas-v3-ink-2)";

  const handleRenameCommit = () => {
    const next = draftTitle.trim();
    if (next && next !== title) {
      onRename(threadId, next);
    }
    setRenaming(false);
  };

  const handleRenameCancel = () => {
    setDraftTitle(title);
    setRenaming(false);
  };

  const handleDeleteClick = () => {
    // Lift the prompt up to the parent rail's themed ConfirmDialogV3. The
    // parent shows the modal and only fires the actual `deleteThread`
    // dispatch on confirm.
    onRequestDelete(threadId, title);
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        width: "100%",
        borderLeft: `4px solid ${borderLeftColor}`,
        background,
        borderRadius: 6,
        transition:
          "background-color 0.12s ease, border-color 0.12s ease",
      }}
    >
      {/* Row body — clickable surface that opens the thread. */}
      <button
        type="button"
        onClick={() => {
          if (!renaming) onOpen(threadId);
        }}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-current={isActive ? "true" : undefined}
        title={title}
        disabled={renaming}
        style={{
          textAlign: "left",
          border: "none",
          background: "transparent",
          width: "100%",
          // Right-pad to clear the menu button (28px button + ~8px margin).
          padding: "8px 36px 8px 10px",
          cursor: renaming ? "default" : "pointer",
          fontFamily: "inherit",
          color: "inherit",
          display: "block",
        }}
      >
        <div
          style={{
            fontFamily:
              '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 10,
            letterSpacing: isPlatform ? "0.08em" : "0.04em",
            color: isPlatform
              ? "var(--midas-v3-accent)"
              : "var(--midas-v3-faint)",
            textTransform: "uppercase",
          }}
        >
          {metaText}
        </div>

        {renaming ? (
          <input
            ref={inputRef}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleRenameCommit();
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleRenameCancel();
              }
            }}
            onBlur={handleRenameCommit}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              marginTop: 1,
              fontSize: 13,
              fontFamily: "inherit",
              color: "var(--midas-v3-ink)",
              background: "var(--midas-v3-card)",
              border: "1px solid var(--midas-v3-accent)",
              borderRadius: 4,
              padding: "1px 6px",
              outline: "none",
              boxSizing: "border-box",
              lineHeight: 1.3,
            }}
            aria-label={renameLabel}
          />
        ) : (
          <div
            style={{
              fontSize: 13,
              color: titleColor,
              marginTop: 1,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              transition: "color 0.12s ease, opacity 0.22s ease",
              // Item 10 — start at 0.6 opacity when we just detected a title
              // change, then fade up to 1.0. `setTitleAnim` flips false after
              // 220ms which lets the transition settle.
              opacity: titleAnim ? 0.6 : 1,
            }}
          >
            {title}
          </div>
        )}
      </button>

      {/* 3-dot menu button — absolute-positioned over the row's right edge.
          Stops propagation so clicking the menu trigger never opens the
          thread. Visible on hover/focus/menu-open.

          Round 6: the menu itself is the custom `RailRowMenuV3` (V3-themed,
          portal'd to body and re-wrapped in `.midas-v3-root` so V3 tokens
          resolve — see component header for the root cause). The previous
          Radix dropdown from `v2/ui` rendered transparent because it
          portalled OUT of `.midas-v3-root`. */}
      <button
        ref={menuTriggerRef}
        type="button"
        aria-label={rowActionsLabel}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((o) => !o);
        }}
        style={{
          position: "absolute",
          right: 4,
          top: "50%",
          transform: "translateY(-50%)",
          width: 28,
          height: 28,
          padding: 0,
          border: "none",
          background: "transparent",
          borderRadius: 4,
          cursor: "pointer",
          opacity: menuButtonOpacity,
          transition: "opacity 0.15s ease, background-color 0.12s ease",
          color: "var(--midas-v3-muted)",
          display: "grid",
          placeItems: "center",
          fontFamily: "inherit",
          fontSize: 14,
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            "color-mix(in oklab, var(--midas-v3-ink) 8%, transparent)";
          e.currentTarget.style.color = "var(--midas-v3-ink)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = "var(--midas-v3-muted)";
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>
          ⋯
        </span>
      </button>

      <RailRowMenuV3
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        triggerRef={menuTriggerRef}
        ariaLabel={rowActionsLabel}
        items={[
          {
            id: "rename",
            label: renameLabel,
            icon: <RailRowMenuV3RenameIcon />,
            onSelect: () => {
              // Defer so the menu's close animation completes + focus moves
              // cleanly back to the row before we enter rename mode.
              window.setTimeout(() => setRenaming(true), 0);
            },
          },
          {
            id: "delete",
            label: deleteLabel,
            icon: <RailRowMenuV3DeleteIcon />,
            destructive: true,
            onSelect: () => {
              window.setTimeout(handleDeleteClick, 0);
            },
          },
        ]}
      />
    </div>
  );
};

// ============================================================
// SkeletonRow — Item 3: rail loading placeholder
// ============================================================

const SkeletonRow: React.FC<{ widthPct: number }> = ({ widthPct }) => (
  <div
    aria-hidden="true"
    style={{
      padding: "8px 20px 8px 14px",
      borderLeft: "4px solid transparent",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    }}
  >
    <div
      style={{
        height: 8,
        width: 60,
        borderRadius: 4,
        background: "var(--midas-v3-card-2)",
        animation: "midas-pulse 1.6s ease-in-out infinite",
      }}
    />
    <div
      style={{
        height: 11,
        width: `${widthPct}%`,
        borderRadius: 4,
        background: "var(--midas-v3-card-2)",
        animation: "midas-pulse 1.6s ease-in-out infinite",
      }}
    />
  </div>
);

const RailSkeletonList: React.FC = () => (
  <div
    role="status"
    aria-label="Loading conversations"
    style={{ display: "flex", flexDirection: "column", gap: 2 }}
  >
    {/* Eyebrow placeholder */}
    <div
      aria-hidden="true"
      style={{
        height: 8,
        width: 48,
        margin: "8px 6px 4px",
        borderRadius: 4,
        background: "var(--midas-v3-card-2)",
        animation: "midas-pulse 1.6s ease-in-out infinite",
      }}
    />
    <SkeletonRow widthPct={80} />
    <SkeletonRow widthPct={65} />
    <SkeletonRow widthPct={72} />
    <SkeletonRow widthPct={55} />
    <SkeletonRow widthPct={68} />
  </div>
);

// ============================================================
// RailEmptyState — Item 4
// ============================================================

const RailEmptyState: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <div
    role="status"
    style={{
      padding: "32px 12px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      textAlign: "center",
    }}
  >
    <div
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: "var(--midas-v3-ink-2)",
      }}
    >
      {title}
    </div>
    <div
      style={{
        fontSize: 11,
        color: "var(--midas-v3-faint)",
      }}
    >
      {body}
    </div>
  </div>
);

// Item 7 — all 5 buckets the rail renders. Previously only the first 3 were
// listed; threads in `lastWeek` / `earlier` were silently dropped from the
// rail (the data is fetched with page size 100 so it's already in state).
const RAIL_BUCKETS: {
  bucket: V3DateBucket;
  labelKey: string;
  fallback: string;
}[] = [
  { bucket: "today", labelKey: "midas.v3.bucketToday", fallback: "Today" },
  {
    bucket: "yesterday",
    labelKey: "midas.v3.bucketYesterday",
    fallback: "Yesterday",
  },
  {
    bucket: "thisWeek",
    labelKey: "midas.v3.bucketThisWeek",
    fallback: "This week",
  },
  {
    bucket: "lastWeek",
    labelKey: "midas.v3.bucketLastWeek",
    fallback: "Last week",
  },
  {
    bucket: "earlier",
    labelKey: "midas.v3.bucketEarlier",
    fallback: "Earlier",
  },
];

// ============================================================
// Helpers
// ============================================================

const flattenMachines = (
  machinesMap: Record<string, Machine[] | undefined> | null | undefined
): Machine[] => {
  if (!machinesMap || typeof machinesMap !== "object") return [];
  const flat: Machine[] = [];
  Object.values(machinesMap).forEach((g) => {
    if (Array.isArray(g)) flat.push(...(g as Machine[]));
  });
  return flat;
};

// ============================================================
// IconBtn — primitive used by both rail variants
// ============================================================

interface IconBtnProps {
  title: string;
  onClick: () => void;
  accent?: boolean;
  size?: number;
  children: React.ReactNode;
}

const IconBtn: React.FC<IconBtnProps> = ({
  title,
  onClick,
  accent = false,
  size = 36,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    style={{
      width: size,
      height: size,
      padding: 0,
      borderRadius: 8,
      background: accent ? "var(--midas-v3-accent)" : "transparent",
      color: accent ? "#fff" : "var(--midas-v3-muted)",
      border: accent
        ? "1px solid var(--midas-v3-accent)"
        : "1px solid transparent",
      display: "grid",
      placeItems: "center",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 16,
      lineHeight: 1,
      transition: "background-color .12s, color .12s, border-color .12s",
    }}
    onMouseEnter={(e) => {
      if (accent) {
        e.currentTarget.style.background = "var(--midas-v3-accent-hov)";
        e.currentTarget.style.borderColor = "var(--midas-v3-accent-hov)";
      } else {
        e.currentTarget.style.background = "var(--midas-v3-card-2)";
        e.currentTarget.style.color = "var(--midas-v3-ink)";
      }
    }}
    onMouseLeave={(e) => {
      if (accent) {
        e.currentTarget.style.background = "var(--midas-v3-accent)";
        e.currentTarget.style.borderColor = "var(--midas-v3-accent)";
      } else {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = "var(--midas-v3-muted)";
      }
    }}
  >
    {children}
  </button>
);

// ============================================================
// Collapsed rail (56px)
// ============================================================

interface CollapsedRailProps {
  onToggle: () => void;
  onNewChat: () => void;
  onBrowseAll: () => void;
  newChatLabel: string;
  browseAllLabel: string;
  expandLabel: string;
}

const CollapsedRail: React.FC<CollapsedRailProps> = ({
  onToggle,
  onNewChat,
  onBrowseAll,
  newChatLabel,
  browseAllLabel,
  expandLabel,
}) => (
  <aside
    className="midas-v3-rail"
    aria-label="Midas history rail (collapsed)"
    style={{
      width: MIDAS_V3_RAIL_COLLAPSED_WIDTH,
      flexShrink: 0,
      padding: "14px 10px",
      background: "var(--midas-v3-bg-soft)",
      borderRight: "1px solid var(--midas-v3-border)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
    }}
  >
    <div style={{ marginBottom: 4 }}>
      <MidasLogoV3 size={32} />
    </div>

    <IconBtn onClick={onToggle} title={`${expandLabel} · ${MOD_LABEL}`}>
      <span style={{ fontSize: 13 }}>▸</span>
    </IconBtn>

    <div
      aria-hidden="true"
      style={{
        height: 1,
        width: 24,
        background: "var(--midas-v3-border-soft)",
        margin: "4px 0",
      }}
    />

    <IconBtn onClick={onNewChat} title={newChatLabel} accent>
      <span style={{ fontSize: 18, lineHeight: 1 }}>＋</span>
    </IconBtn>
    <IconBtn onClick={onBrowseAll} title={`${browseAllLabel} · ${MOD_LABEL_K}`}>
      <span style={{ fontSize: 13 }}>⌕</span>
    </IconBtn>
  </aside>
);

// ============================================================
// Expanded rail (248px)
// ============================================================

interface MidasHistoryRailV3Props {
  /**
   * Open the New Chat dialog. Owned by `MidasV3Layout`. Both the expanded
   * pill button and the collapsed `＋` icon button dispatch this.
   * Defaults to a no-op so the rail can still render outside the v3 layout.
   */
  onNewChat?: () => void;
}

const MidasHistoryRailV3: React.FC<MidasHistoryRailV3Props> = ({
  onNewChat,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useTranslation("chatBot");

  const collapsed = useAppSelector((s) => s.midasV3.railCollapsed);
  const threads = useAppSelector((s) => s.midasHistoryPage.threads);
  const total = useAppSelector((s) => s.midasHistoryPage.globalTotal);
  const loading = useAppSelector((s) => s.midasHistoryPage.loading);
  const lastFetchedAt = useAppSelector(
    (s) => s.midasHistoryPage.lastFetchedAt
  );
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);

  // ── Self-heal effect (MIDAS-V3-BUGS-0519 sub-task 2) ─────
  // Re-fetches when threads is wiped (e.g. history page unmount calling
  // `resetHistoryPage` clears the list).
  useEffect(() => {
    if (threads.length === 0 && !loading) {
      const args: { machineFilter: HistoryMachineFilter } = {
        machineFilter: "all",
      };
      dispatch(fetchHistoryThreads(args));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads.length, loading]);

  // ── Item 2 — focus re-hydrate ────────────────────────────
  // When the tab regains focus and the last fetch is >60s old, fire an
  // unfiltered re-fetch. `lastFetchedAt` lives in the slice so it persists
  // across rail re-mounts. Guarded by `loading` so we never stack requests.
  // Latest values are read via refs so we don't re-bind the listener on
  // every render.
  const loadingRef = useRef(loading);
  const lastFetchedAtRef = useRef(lastFetchedAt);
  loadingRef.current = loading;
  lastFetchedAtRef.current = lastFetchedAt;

  useEffect(() => {
    const handleFocus = () => {
      if (loadingRef.current) return;
      const ts = lastFetchedAtRef.current;
      if (ts == null) return; // first fetch hasn't completed; self-heal will pick it up.
      if (Date.now() - ts < FOCUS_REHYDRATE_STALE_MS) return;
      dispatch(fetchHistoryThreads({ machineFilter: "all" }));
    };
    window.addEventListener("focus", handleFocus);
    // `visibilitychange` also fires when alt-tabbing on some browsers; the
    // user is most likely to drift away via tab switch / app switch where
    // focus is the more reliable signal, but cover both for safety.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") handleFocus();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [dispatch]);

  const machineNameById = useMemo(() => {
    const map: Record<string, string> = {};
    flattenMachines(machinesMap).forEach((m) => {
      const brand = m.customBrandName || m.manufacturer?.name || "";
      const name = brand
        ? `${brand} ${m.model || ""}`.trim()
        : m.model || "";
      if (m.id && name) map[m.id] = name;
    });
    return map;
  }, [machinesMap]);

  // Item 7: render all 5 buckets (today / yesterday / thisWeek / lastWeek /
  // earlier). Page-size 100 means the rail already has the data.
  const grouped = useMemo(() => {
    const sorted = [...threads].sort(
      (a, b) =>
        new Date(b.last_message_at || b.created_at).getTime() -
        new Date(a.last_message_at || a.created_at).getTime()
    );

    const buckets: Record<V3DateBucket, typeof threads> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      lastWeek: [],
      earlier: [],
    };
    sorted.forEach((t) => {
      const b = bucketOfV3(t.last_message_at || t.created_at);
      buckets[b].push(t);
    });
    return buckets;
  }, [threads]);

  const handleToggle = () => dispatch(toggleRailCollapsed());

  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      navigate("/midas-v3");
    }
  };

  const handleBrowseAll = () => navigate("/midas-v3/history");

  const handleOpenThread = (threadId: string) => {
    dispatch(loadThread(threadId));
    navigate("/midas-v3");
  };

  const handleRenameRow = useCallback(
    (id: string, newTitle: string) => {
      dispatch(renameThread({ id, title: newTitle }));
    },
    [dispatch]
  );

  // ── Themed delete-confirm dialog state ───────────────────
  // Lifted to the rail level so a single `ConfirmDialogV3` instance handles
  // every row. The row that opened the dialog is identified by `threadId`;
  // the row's title is captured at open-time so the dialog can render it in
  // the body copy even if `threads[]` reshuffles while the dialog is open.
  const [deleteTarget, setDeleteTarget] = useState<{
    threadId: string;
    title: string;
  } | null>(null);

  const handleRequestDelete = useCallback(
    (threadId: string, threadTitle: string) => {
      setDeleteTarget({ threadId, title: threadTitle });
    },
    []
  );

  const handleCancelDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    const id = deleteTarget.threadId;
    dispatch(deleteThread(id));
    // If the user just deleted the thread that was loaded into the chat
    // surface, clear `activeThreadId` so the chat header doesn't keep
    // showing a phantom title.
    if (id === activeThreadId) {
      dispatch(setActiveThreadId(null));
    }
    setDeleteTarget(null);
  }, [deleteTarget, dispatch, activeThreadId]);

  // Translations / labels (passed down so RailRow / states don't each call
  // translate themselves).
  const brandLabel = translate("midas.v3.brandTitle", {
    defaultValue: "Midas",
  }) as string;
  const versionChip = translate("midas.v3.versionChip", {
    defaultValue: "v3",
  }) as string;
  const newChatLabel = translate("midas.v3.newChat", {
    defaultValue: "New chat",
  }) as string;
  const browseAllLabel = translate("midas.v3.browseAllHistory", {
    defaultValue: "Browse all history",
  }) as string;
  const collapseLabel = translate("midas.v3.collapseRail", {
    defaultValue: "Collapse sidebar",
  }) as string;
  const expandLabel = translate("midas.v3.expandRail", {
    defaultValue: "Expand sidebar",
  }) as string;
  const rowActionsLabel = translate("midas.v3.railRowActions", {
    defaultValue: "Conversation actions",
  }) as string;
  const renameLabel = translate("midas.v3.railRename", {
    defaultValue: "Rename",
  }) as string;
  const deleteLabel = translate("midas.v3.railDelete", {
    defaultValue: "Delete",
  }) as string;
  // Round 6: dedicated dialog copy. The legacy `railDeleteConfirm` key is no
  // longer referenced (it was the native `window.confirm` body). Kept in
  // locale files in case a future surface needs the short-form prompt.
  const deleteDialogTitle = translate("midas.v3.railDeleteDialogTitle", {
    defaultValue: "Delete conversation?",
  }) as string;
  const deleteDialogBodyFallback = translate(
    "midas.v3.railDeleteDialogBodyFallback",
    {
      defaultValue:
        "This will permanently delete this conversation. This can't be undone.",
    }
  ) as string;
  const deleteDialogCancelLabel = translate(
    "midas.v3.railDeleteDialogCancel",
    {
      defaultValue: "Cancel",
    }
  ) as string;
  const deleteDialogConfirmLabel = translate(
    "midas.v3.railDeleteDialogConfirm",
    {
      defaultValue: "Delete",
    }
  ) as string;
  // i18next interpolates `{{title}}` when we pass it as a translate option.
  // The `with-title` key includes the placeholder; the fallback variant
  // (above) is used when the title is empty/whitespace.
  const buildDeleteDialogBody = (threadTitle: string): string => {
    const trimmed = threadTitle.trim();
    if (!trimmed) return deleteDialogBodyFallback;
    return translate("midas.v3.railDeleteDialogBodyWithTitle", {
      title: trimmed,
      defaultValue:
        'This will permanently delete the conversation "{{title}}". This can\'t be undone.',
    }) as string;
  };
  const emptyTitle = translate("midas.v3.railEmptyTitle", {
    defaultValue: "No conversations yet",
  }) as string;
  const emptyBody = translate("midas.v3.railEmptyBody", {
    defaultValue: "Start one above",
  }) as string;

  if (collapsed) {
    return (
      <CollapsedRail
        onToggle={handleToggle}
        onNewChat={handleNewChat}
        onBrowseAll={handleBrowseAll}
        newChatLabel={newChatLabel}
        browseAllLabel={browseAllLabel}
        expandLabel={expandLabel}
      />
    );
  }

  // Item 3 + Item 4: pick which body to render in the scrollable list.
  // Loading & empty → skeleton; loaded & empty → empty state; loaded with
  // rows → the bucket list.
  const isInitialLoading = loading && threads.length === 0;
  const isLoadedEmpty = !loading && threads.length === 0;

  return (
    <aside
      className="midas-v3-rail"
      aria-label="Midas history rail"
      style={{
        width: MIDAS_V3_RAIL_EXPANDED_WIDTH,
        flexShrink: 0,
        padding: "16px 12px",
        background: "var(--midas-v3-bg-soft)",
        borderRight: "1px solid var(--midas-v3-border)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflow: "hidden",
      }}
    >
      {/* Brand row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "2px 4px",
        }}
      >
        <MidasLogoV3 size={28} />
        <div style={{ fontWeight: 600, fontSize: 14 }}>{brandLabel}</div>
        <span
          className="mono"
          style={{
            marginLeft: "auto",
            fontSize: 10,
            color: "var(--midas-v3-faint)",
          }}
        >
          {versionChip}
        </span>
        <MidasThemeSwitcherV3 />
        <button
          type="button"
          onClick={handleToggle}
          title={`${collapseLabel} · ${MOD_LABEL}`}
          aria-label={`${collapseLabel} · ${MOD_LABEL}`}
          style={{
            width: 24,
            height: 24,
            padding: 0,
            borderRadius: 6,
            background: "transparent",
            border: "1px solid transparent",
            color: "var(--midas-v3-muted)",
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontFamily: "inherit",
            transition: "background-color .12s, color .12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--midas-v3-card-2)";
            e.currentTarget.style.color = "var(--midas-v3-ink)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--midas-v3-muted)";
          }}
        >
          ◂
        </button>
      </div>

      {/* New chat + Browse all */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <button
          type="button"
          onClick={handleNewChat}
          aria-label={newChatLabel}
          style={{
            width: "100%",
            height: 32,
            padding: "0 11px",
            borderRadius: 7,
            background: "var(--midas-v3-accent)",
            color: "#fff",
            border: "1px solid var(--midas-v3-accent)",
            fontFamily: "inherit",
            fontSize: 12.5,
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            cursor: "pointer",
            transition: "background-color .12s, border-color .12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--midas-v3-accent-hov)";
            e.currentTarget.style.borderColor = "var(--midas-v3-accent-hov)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--midas-v3-accent)";
            e.currentTarget.style.borderColor = "var(--midas-v3-accent)";
          }}
        >
          ＋ {newChatLabel}
        </button>
        <button
          type="button"
          onClick={handleBrowseAll}
          aria-label={`${browseAllLabel} · ${MOD_LABEL_K}`}
          style={{
            width: "100%",
            height: 30,
            padding: "0 11px",
            borderRadius: 7,
            background: "var(--midas-v3-card)",
            border: "1.5px solid var(--midas-v3-border)",
            boxShadow: "0 1px 0 rgba(0, 0, 0, 0.02)",
            color: "var(--midas-v3-muted)",
            fontFamily: "inherit",
            fontSize: 12,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 6,
            cursor: "pointer",
            transition:
              "background-color .12s, color .12s, border-color .12s, box-shadow .12s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--midas-v3-accent-soft)";
            e.currentTarget.style.color = "var(--midas-v3-accent)";
            e.currentTarget.style.borderColor = "var(--midas-v3-accent)";
            e.currentTarget.style.boxShadow =
              "0 1px 0 color-mix(in oklab, var(--midas-v3-accent) 8%, transparent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--midas-v3-card)";
            e.currentTarget.style.color = "var(--midas-v3-muted)";
            e.currentTarget.style.borderColor = "var(--midas-v3-border)";
            e.currentTarget.style.boxShadow = "0 1px 0 rgba(0, 0, 0, 0.02)";
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              minWidth: 0,
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {browseAllLabel}
            </span>
            <span
              className="mono"
              style={{ color: "var(--midas-v3-faint)", fontSize: 11 }}
            >
              {total}
            </span>
          </span>
          <span
            className="mono"
            style={{ color: "var(--midas-v3-faint)", fontSize: 11 }}
          >
            {MOD_LABEL_K}
          </span>
        </button>
      </div>

      {/* Scrollable history list (or skeleton / empty-state) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflowY: "auto",
          overflowX: "hidden",
          marginTop: 2,
        }}
      >
        {isInitialLoading ? (
          <RailSkeletonList />
        ) : isLoadedEmpty ? (
          <RailEmptyState title={emptyTitle} body={emptyBody} />
        ) : (
          RAIL_BUCKETS.map(({ bucket, labelKey, fallback }) => {
            const items = grouped[bucket];
            if (!items || items.length === 0) return null;
            const label = translate(labelKey as any, {
              defaultValue: fallback,
            }) as string;
            return (
              <React.Fragment key={bucket}>
                <div
                  className="midas-v3-eyebrow"
                  style={{ padding: "8px 6px 4px" }}
                >
                  {label}
                </div>
                {items.map((t) => {
                  const isActive = t.id === activeThreadId;
                  const isPlatform = !t.machine_id;
                  const machineLabel = t.machine_id
                    ? machineNameById[t.machine_id] || null
                    : null;
                  const metaText = isPlatform
                    ? "◆ PLATFORM"
                    : machineLabel || "";
                  return (
                    <RailRow
                      key={t.id}
                      threadId={t.id}
                      title={t.title}
                      metaText={metaText}
                      isPlatform={isPlatform}
                      isActive={isActive}
                      onOpen={handleOpenThread}
                      onRename={handleRenameRow}
                      onRequestDelete={handleRequestDelete}
                      rowActionsLabel={rowActionsLabel}
                      renameLabel={renameLabel}
                      deleteLabel={deleteLabel}
                    />
                  );
                })}
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Footer with Ctrl+K hint */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 10,
          borderTop: "1px solid var(--midas-v3-border-soft)",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 11, color: "var(--midas-v3-faint)" }}
        >
          {MOD_LABEL_K}
        </span>
      </div>

      {/* Phase 1 doesn't change location-driven behaviour, but reading the
          pathname here keeps lint quiet and gives Phase 5 a clean hook. */}
      <span aria-hidden="true" style={{ display: "none" }}>
        {location.pathname}
      </span>

      {/* Round 6: themed delete-confirm dialog. Owned at the rail level so a
          single instance handles every row. Replaces the previous
          `window.confirm` call which Chrome rendered with its dark, non-V3
          system dialog (see screenshot `wrong.PNG`). */}
      <ConfirmDialogV3
        open={deleteTarget !== null}
        title={deleteDialogTitle}
        body={
          deleteTarget
            ? buildDeleteDialogBody(deleteTarget.title)
            : deleteDialogBodyFallback
        }
        cancelLabel={deleteDialogCancelLabel}
        confirmLabel={deleteDialogConfirmLabel}
        destructive
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </aside>
  );
};

export default MidasHistoryRailV3;
